export const routes = {
  RESET_PASSWORD: '/reset-password',
}

export const platforms = ['instagram', 'facebook', 'tiktok', 'twitter', 'youtube', 'pinterest']

export const gender = ['male', 'female']

export const accountGenders = ['Male', 'Female', 'Gender Neutral']

export const userType = {
  athlete: 'athlete',
  brand: 'brand',
  marketer: 'marketer', // Added for consistency with marketer-flow
  influencer: 'influencer',
  agency: 'agency',
  manager: 'manager', // Added for manager-flow
  admin: 'admin',
  school_admin: 'school_admin', // School administrator portal
  proxy_athlete: 'proxy_athlete',
}

export const athleteUserTypes = [userType.athlete, userType.proxy_athlete] as const

export const getAllAthleteUserTypes = () => [...athleteUserTypes]

export const hashPrefixes = {
  user_info: 'user',
  social_account_info: 'social_account',
  content_info: 'content',
  creator_payment_methods: 'creator_payment',
  brand_info: 'brand',
  org_info: 'org',
  campaign_info: 'campaign',
  wave_info: 'wave',
  collaboration_info: 'collaboration',
  auth_token_info: 'auth_token',
}

export const bcrypt = {
  saltRounds: 10,
}

export const linodeOptions = {
  LINODE_BUCKET_NAME: 'adthlete',
  LINODE_BUCKET_REGION: 'us-iad-1',
}

export const facebook = {
  instagram_scopes: [
    'public_profile',
    'email',
    'instagram_basic',
    'read_insights',
    'pages_read_engagement',
    'pages_read_user_content',
    'instagram_manage_insights',
    'business_management',
  ],
  scopes: [
    'public_profile',
    'email',
    'read_insights',
    'pages_read_engagement',
    'pages_read_user_content',
  ],
}

export const mail = {
  from: `Athios <no-reply@athios.ai>`,
  logo_url: 'https://adthlete.us-iad-1.linodeobjects.com/public/athios-logo-black.png',
}

export const magicLink = {
  expirationMinutes: 15, // Magic link tokens expire after 15 minutes
  rateLimitMinutes: 5, // Users can request a new magic link every 5 minutes
}

export const app = {
  url: import.meta.env.VITE_APP_URL ?? 'https://app.athios.ai',
  backend_url: import.meta.env.VITE_BACKEND_URL ?? 'https://api.athios.ai',
}

export const subDomains = {
  admin: 'admin',
  athlete: 'app',
  brand: 'marketer',
  marketer: 'marketer',
  manager: 'manager',
  school_admin: 'school',
}

export const env_url_prefixes = {
  development: {
    admin: 'http://admin-frontend.localhost:1355',
    athlete: 'http://athlete-flow.localhost:1355',
    marketer: 'http://marketer-flow.localhost:1355',
    manager: 'http://manager-flow.localhost:1355',
    school_admin: 'http://school-admin.localhost:1355',
  },
  test: {
    admin: 'http://admin-frontend.localhost:1355',
    athlete: 'http://athlete-flow.localhost:1355',
    marketer: 'http://marketer-flow.localhost:1355',
    manager: 'http://manager-flow.localhost:1355',
    school_admin: 'http://school-admin.localhost:1355',
  },
  staging: {
    admin: 'https://adthlete-admin-staging.lunaparkdigital.com',
    athlete: 'https://athlete-flow.lunaparkdigital.com',
    brand: 'https://marketer-flow.lunaparkdigital.com',
    marketer: 'https://marketer-flow.lunaparkdigital.com',
    manager: 'https://manager-flow.lunaparkdigital.com',
    school_admin: 'https://school-admin.lunaparkdigital.com',
  },
  production: {
    admin: 'https://admin.athios.ai',
    athlete: 'https://app.athios.ai',
    brand: 'https://marketer.athios.ai',
    marketer: 'https://marketer.athios.ai',
    manager: 'https://manager.athios.ai',
    school_admin: 'https://school.athios.ai',
  },
  dev: {
    admin: 'https://dev.lunaparkdigital.com/admin',
    athlete: 'https://dev.lunaparkdigital.com/athlete',
    brand: 'https://dev.lunaparkdigital.com/marketer',
    marketer: 'https://dev.lunaparkdigital.com/marketer',
    manager: 'https://dev.lunaparkdigital.com/manager',
    school_admin: 'https://dev.lunaparkdigital.com/school-admin',
  },
  marketplace: {
    admin: 'https://marketplace.lunaparkdigital.com/admin',
    athlete: 'https://marketplace.lunaparkdigital.com/athlete',
    brand: 'https://marketplace.lunaparkdigital.com/brand',
    marketer: 'https://marketplace.lunaparkdigital.com/marketer',
    manager: 'https://marketplace.lunaparkdigital.com/manager',
    school_admin: 'https://marketplace.lunaparkdigital.com/school_admin',
  },
}

export const getAppPrefix = ({ env, appName }: { env: string; appName: string }) => {
  // @ts-ignore
  const prefixes = env_url_prefixes[env || 'development']
  // @ts-ignore
  return prefixes[appName || 'athlete']
}

export const media_types = {
  yt_short: 1,
  yt_video: 2,
  ig_story: 3,
  ig_carousel: 4,
  ig_reel: 5,
  tt_video: 6,
}

export const linodeS3 = {
  bucketName: linodeOptions.LINODE_BUCKET_NAME,
  region: linodeOptions.LINODE_BUCKET_REGION,
}

export const marketerFrontendUrl = 'https://marketer.athios.ai'

export const managerFrontendUrl = 'https://manager.athios.ai'

export const BACKEND_PORT = 3099

export const magicLinkTokenExpirationMinutes = 15

export const TPP_LOGO_URL =
  'https://tpp-content.us-east-1.linodeobjects.com/assets/tpp_logo_square.png'

export const stripe_urls: Record<
  string,
  { STRIPE_RETURN_URL: string; STRIPE_REFRESH_URL: string }
> = {
  development: {
    STRIPE_RETURN_URL: 'http://athlete-flow.localhost:1355/earnings/stripe-return',
    STRIPE_REFRESH_URL: 'http://athlete-flow.localhost:1355/earnings/stripe-refresh',
  },
  staging: {
    STRIPE_RETURN_URL: 'https://athlete-flow.lunaparkdigital.com/earnings/stripe-return',
    STRIPE_REFRESH_URL: 'https://athlete-flow.lunaparkdigital.com/earnings/stripe-refresh',
  },
  production: {
    STRIPE_RETURN_URL: 'https://app.athios.ai/earnings/stripe-return',
    STRIPE_REFRESH_URL: 'https://app.athios.ai/earnings/stripe-refresh',
  },
}

// const config = {
//   routes,
//   platforms,
//   gender,
//   accountGenders,
//   userType,
//   athleteUserTypes,
//   getAllAthleteUserTypes,
//   hashPrefixes,
//   bcrypt,
//   linodeOptions,
//   facebook,
//   mail,
//   magicLink,
//   app,
//   magic_link_urls,
//   media_types,
//   linodeS3,
//   marketerFrontendUrl,
//   managerFrontendUrl,
//   BACKEND_PORT,
//   magicLinkTokenExpirationMinutes,
//   subDomains,
//   TPP_LOGO_URL,
// }

// export default config
